import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  TextInput,
  Switch,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, OptionListType, OptionListEntry } from '../types';
import { optionListsService } from '../services/optionLists';
import { adminService } from '../services/admin';
import { useDialog } from '../contexts';
import { Colors } from '../constants';
import { Button } from '../components';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminOptionLists'>;

const LIST_TYPES: { key: OptionListType; labelKey: string }[] = [
  { key: 'category', labelKey: 'screens.adminOptionLists.tabs.category' },
  { key: 'condition', labelKey: 'screens.adminOptionLists.tabs.condition' },
  { key: 'size', labelKey: 'screens.adminOptionLists.tabs.size' },
  { key: 'sport_type', labelKey: 'screens.adminOptionLists.tabs.sportType' },
];

export const AdminOptionListsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [activeTab, setActiveTab] = useState<OptionListType>('category');
  const [options, setOptions] = useState<OptionListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingOption, setEditingOption] = useState<OptionListEntry | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Form state
  const [formValue, setFormValue] = useState('');
  const [formLabelEn, setFormLabelEn] = useState('');
  const [formLabelNl, setFormLabelNl] = useState('');
  const [formSortOrder, setFormSortOrder] = useState('0');
  const [formIsActive, setFormIsActive] = useState(true);

  // Route guard
  useEffect(() => {
    const checkAdminStatus = async () => {
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        await dialog.alert(
          t('common.errors.unauthorized'),
          t('screens.adminDashboard.notAuthorized'),
          'error'
        );
        navigation.goBack();
      }
    };
    checkAdminStatus();
  }, []);

  // Load options for active tab
  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await optionListsService.getAllOptionsAdmin(activeTab);
      setOptions(data);
    } catch (error) {
      console.error('Failed to load options:', error);
      dialog.alert(t('common.errors.generic'), String(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const resetForm = () => {
    setFormValue('');
    setFormLabelEn('');
    setFormLabelNl('');
    setFormSortOrder('0');
    setFormIsActive(true);
    setEditingOption(null);
    setIsAddingNew(false);
  };

  const openEditForm = (option: OptionListEntry) => {
    setEditingOption(option);
    setFormValue(option.value);
    setFormLabelEn(option.label_en);
    setFormLabelNl(option.label_nl);
    setFormSortOrder(String(option.sort_order));
    setFormIsActive(option.is_active);
    setIsAddingNew(false);
  };

  const openAddForm = () => {
    resetForm();
    setIsAddingNew(true);
  };

  const handleSave = async () => {
    if (!formValue.trim() || !formLabelEn.trim() || !formLabelNl.trim()) {
      dialog.alert(
        t('common.validation.required'),
        t('screens.adminOptionLists.form.allFieldsRequired'),
        'warning'
      );
      return;
    }

    try {
      if (editingOption) {
        await optionListsService.updateOption(editingOption.id, {
          label_en: formLabelEn.trim(),
          label_nl: formLabelNl.trim(),
          sort_order: parseInt(formSortOrder) || 0,
          is_active: formIsActive,
        });
      } else {
        await optionListsService.createOption({
          list_type: activeTab,
          value: formValue.toLowerCase().trim(),
          label_en: formLabelEn.trim(),
          label_nl: formLabelNl.trim(),
          sort_order: parseInt(formSortOrder) || 0,
          is_active: formIsActive,
          scope_type: null,
          scope_value: null,
        });
      }

      resetForm();
      await loadOptions();
      dialog.alert(
        t('common.success'),
        t('screens.adminOptionLists.form.saveSuccess'),
        'success'
      );
    } catch (error) {
      console.error('Failed to save option:', error);
      dialog.alert(t('common.errors.generic'), String(error), 'error');
    }
  };

  const handleDelete = async (option: OptionListEntry) => {
    const confirmed = await dialog.confirm(
      t('screens.adminOptionLists.delete.title'),
      t('screens.adminOptionLists.delete.message', { value: option.label_en }),
      { variant: 'danger' }
    );

    if (confirmed) {
      try {
        await optionListsService.deleteOption(option.id);
        await loadOptions();
        dialog.alert(
          t('common.success'),
          t('screens.adminOptionLists.delete.success'),
          'success'
        );
      } catch (error) {
        console.error('Failed to delete option:', error);
        dialog.alert(t('common.errors.generic'), String(error), 'error');
      }
    }
  };

  return (
    <View style={styles.wrapper}>
      {/* Header */}
      <View style={[styles.headerContainer, Platform.OS === 'web' && styles.headerContainerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}
          >
            <Ionicons name="arrow-back" size={24} color={Colors.primary} />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('screens.adminOptionLists.title')}</Text>
        </View>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        {LIST_TYPES.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, activeTab === tab.key && styles.tabActive]}
            onPress={() => setActiveTab(tab.key)}
          >
            <Text style={[styles.tabText, activeTab === tab.key && styles.tabTextActive]}>
              {t(tab.labelKey)}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
        <View style={styles.contentContainer}>
          {/* Add button */}
          <Button
            title={t('screens.adminOptionLists.addOption')}
            onPress={openAddForm}
            style={styles.addButton}
          />

          {/* Options list */}
          {loading ? (
            <Text style={styles.loadingText}>{t('common.loading')}</Text>
          ) : (
            <View style={styles.optionsList}>
              {options.map((option) => (
                <View key={option.id} style={styles.optionRow}>
                  <View style={styles.optionInfo}>
                    <Text style={styles.optionValue}>{option.value}</Text>
                    <Text style={styles.optionLabels}>
                      EN: {option.label_en} | NL: {option.label_nl}
                    </Text>
                    <Text style={styles.optionMeta}>
                      Sort: {option.sort_order} | {option.is_active ? 'Active' : 'Inactive'}
                    </Text>
                  </View>
                  <View style={styles.optionActions}>
                    <TouchableOpacity onPress={() => openEditForm(option)}>
                      <Ionicons name="pencil" size={20} color={Colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => handleDelete(option)} style={styles.deleteButton}>
                      <Ionicons name="trash" size={20} color={Colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Edit/Add Form */}
          {(editingOption || isAddingNew) && (
            <View style={styles.formContainer}>
              <Text style={styles.formTitle}>
                {editingOption
                  ? t('screens.adminOptionLists.form.editTitle')
                  : t('screens.adminOptionLists.form.addTitle')}
              </Text>

              <Text style={styles.formLabel}>{t('screens.adminOptionLists.form.value')}</Text>
              <TextInput
                style={[styles.formInput, editingOption && styles.formInputDisabled]}
                value={formValue}
                onChangeText={setFormValue}
                editable={!editingOption}
                placeholder="e.g., hoodie"
              />

              <Text style={styles.formLabel}>{t('screens.adminOptionLists.form.labelEn')}</Text>
              <TextInput
                style={styles.formInput}
                value={formLabelEn}
                onChangeText={setFormLabelEn}
                placeholder="English label"
              />

              <Text style={styles.formLabel}>{t('screens.adminOptionLists.form.labelNl')}</Text>
              <TextInput
                style={styles.formInput}
                value={formLabelNl}
                onChangeText={setFormLabelNl}
                placeholder="Dutch label"
              />

              <Text style={styles.formLabel}>{t('screens.adminOptionLists.form.sortOrder')}</Text>
              <TextInput
                style={styles.formInput}
                value={formSortOrder}
                onChangeText={setFormSortOrder}
                keyboardType="numeric"
                placeholder="0"
              />

              <View style={styles.switchRow}>
                <Text style={styles.formLabel}>{t('screens.adminOptionLists.form.active')}</Text>
                <Switch value={formIsActive} onValueChange={setFormIsActive} />
              </View>

              <View style={styles.formButtons}>
                <Button title={t('common.buttons.save')} onPress={handleSave} />
                <Button
                  title={t('common.buttons.cancel')}
                  onPress={resetForm}
                  variant="outline"
                  style={styles.cancelButton}
                />
              </View>
            </View>
          )}

          {/* Link to Custom Values */}
          <TouchableOpacity
            style={styles.customValuesLink}
            onPress={() => navigation.navigate('AdminCustomValues')}
          >
            <Text style={styles.customValuesLinkText}>
              {t('screens.adminOptionLists.viewCustomValues')} →
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  headerContainer: {
    backgroundColor: Colors.white,
    padding: 16,
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  headerContainerWeb: {
    alignItems: 'center',
  },
  headerContent: {
    width: '100%',
    maxWidth: 1200,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  backText: {
    color: '#007AFF',
    fontSize: 16,
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.text,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: Colors.primary,
  },
  tabText: {
    fontSize: 14,
    color: Colors.textMedium,
  },
  tabTextActive: {
    color: Colors.primary,
    fontWeight: '600',
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    ...(Platform.OS === 'web' && {
      alignItems: 'center' as const,
    }),
  },
  contentContainer: {
    width: '100%',
    padding: 16,
    ...(Platform.OS === 'web' && {
      maxWidth: 1200,
    }),
  },
  addButton: {
    marginBottom: 16,
  },
  loadingText: {
    textAlign: 'center',
    color: Colors.textMedium,
    padding: 20,
  },
  optionsList: {
    backgroundColor: Colors.white,
    borderRadius: 8,
    overflow: 'hidden',
  },
  optionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  optionInfo: {
    flex: 1,
  },
  optionValue: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text,
  },
  optionLabels: {
    fontSize: 14,
    color: Colors.textMedium,
    marginTop: 2,
  },
  optionMeta: {
    fontSize: 12,
    color: Colors.textLight,
    marginTop: 2,
  },
  optionActions: {
    flexDirection: 'row',
    gap: 12,
  },
  deleteButton: {
    marginLeft: 12,
  },
  formContainer: {
    backgroundColor: Colors.white,
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
  },
  formTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text,
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.textDark,
    marginBottom: 4,
    marginTop: 12,
  },
  formInput: {
    borderWidth: 1,
    borderColor: Colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: Colors.text,
    backgroundColor: Colors.white,
  },
  formInputDisabled: {
    backgroundColor: Colors.backgroundLight,
    color: Colors.textMedium,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  formButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 20,
  },
  cancelButton: {
    flex: 0,
  },
  customValuesLink: {
    marginTop: 24,
    padding: 16,
    backgroundColor: Colors.backgroundLight,
    borderRadius: 8,
    alignItems: 'center',
  },
  customValuesLinkText: {
    color: Colors.primary,
    fontSize: 16,
    fontWeight: '500',
  },
});
